// TODO: Maybe make this support horizontal scroll at some point.
// This component renders an accessible and customizable scrollable container. The scrollbar will be rendered with a regular div instead of the browser's scrollbar. This will allow us to control the height and other neat properties.
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

type Props = React.HTMLProps<HTMLDivElement> & {
  sneakyPeakyHeight?: number;
  trackWidthClass: string;
  trackHandleWidthClass: string;
  dataTestId?: string;
};

const computeScrollPercentage = (props: {
  scrollHeight: number;
  scrollTop: number;
  assumedViewportHeight: number;
}): number => {
  const { scrollHeight, assumedViewportHeight, scrollTop } = props;
  // Now just figure out our exact scroll percentage.
  const denominator = scrollHeight - assumedViewportHeight;

  return denominator === 0 ? 1 : scrollTop / denominator;
};

/**
 * This is a custom scroll component that we use for consistency, as well as being able to predict the size of
 * our layout more easily. Visually, it mirrors the style of the pipeline analysis scrollbar for key takeaways.
 * That said, you can pass in a few values to configure it.
 */
export const Scrollable: React.FC<Props> = React.forwardRef((props, ref) => {
  const {
    children,
    sneakyPeakyHeight,
    trackWidthClass,
    trackHandleWidthClass,
    dataTestId,
    ...rest
  } = props;

  const contentRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    ref,
    () => scrollRef.current,
  );

  const onLayout = useCallback(() => {
    if (
      !contentRef.current ||
      !trackRef.current ||
      !thumbRef.current ||
      !scrollRef.current ||
      scrollRef.current.clientHeight <= 0
    ) {
      return;
    }

    // Since this component supports the notion of "peeking under" the scrollable, we need to account for that first.
    // We call this extra space the "sneaky peaky height".
    const assumedViewportHeight = scrollRef.current.parentElement
      ? scrollRef.current.parentElement.clientHeight
      : 0;

    const viewportHeight = assumedViewportHeight - (sneakyPeakyHeight ?? 0);
    const scrollHeight = scrollRef.current.scrollHeight;

    const scrollPercentage = computeScrollPercentage({
      scrollHeight: scrollHeight,
      scrollTop: scrollRef.current.scrollTop,
      assumedViewportHeight,
    });

    // The max thumb top is the viewport height minus the thumb height.
    const maxThumbTop = viewportHeight - thumbRef.current.clientHeight;

    // Now just set the thumb's top to the scroll percentage times the max thumb top.
    thumbRef.current.style.top = scrollPercentage * maxThumbTop + 'px';

    // Compute the size of the thumb, which should be proportional to the viewport height.
    const thumbHeight = (viewportHeight / scrollHeight) * 100;
    thumbRef.current.style.height = thumbHeight + '%';

    if (viewportHeight >= scrollHeight - (sneakyPeakyHeight ?? 0)) {
      trackRef.current.style.display = 'none';
    } else {
      trackRef.current.style.display = 'block';
    }
  }, [sneakyPeakyHeight]);

  useEffect(() => {
    onLayout();
  }, [onLayout]);

  useEffect(() => {
    if (scrollRef.current) {
      onLayout();
    }
  }, [children, onLayout]);

  const [isDragging, setIsDragging] = useState(false);
  const [scrollStartPosition, setScrollStartPosition] = useState(0);
  const [initialScrollTop, setInitialScrollTop] = useState(0);

  const handleThumbMousedown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientY);
    setInitialScrollTop(scrollRef.current?.scrollTop ?? 0);
    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging],
  );

  const handleThumbMousemove = useCallback(
    (e: MouseEvent) => {
      // skip callback if user is dragging on a color picker, otherwise this callback will
      // disable dragging there
      const el = e.target as HTMLElement;
      const isFromColorPicker = el.closest('.w-color-sketch, .chrome-picker');

      if (isFromColorPicker) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      if (!scrollRef.current || !thumbRef.current) {
        return;
      }

      if (isDragging) {
        const deltaY = e.clientY - scrollStartPosition;
        const viewportHeight =
          scrollRef.current.clientHeight - (sneakyPeakyHeight ?? 0);

        const maxThumbTop = viewportHeight - thumbRef.current.clientHeight;
        const maxScrollTop =
          scrollRef.current.scrollHeight - scrollRef.current.clientHeight;
        const scrollPercentage = deltaY / maxThumbTop;
        scrollRef.current.scrollTop =
          initialScrollTop + scrollPercentage * maxScrollTop;
      }
    },
    [isDragging, scrollStartPosition, initialScrollTop, sneakyPeakyHeight],
  );

  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);
    document.addEventListener('resize', onLayout);
    window.addEventListener('resize', onLayout);
    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
      document.removeEventListener('resize', onLayout);
      window.removeEventListener('resize', onLayout);
    };
  }, [handleThumbMousemove, handleThumbMouseup, onLayout]);
  return (
    <div className={'relative w-full h-full'}>
      <div
        {...rest}
        className={
          props.className +
          ' overflow-y-scroll no-scrollbar w-full h-full absolute left-0 top-0'
        }
        ref={scrollRef}
        data-testid={dataTestId}
        onScroll={onLayout}
      >
        <div className="relative">
          <div ref={contentRef}>{children}</div>
        </div>
      </div>
      <div
        ref={trackRef}
        className={`absolute right-0 top-0 h-full ${trackWidthClass}`}
      >
        <div
          ref={thumbRef}
          onMouseDown={handleThumbMousedown}
          className="absolute left-0 w-full pl-[4px] cursor-pointer"
        >
          <div
            className={`rounded-full bg-gray-300 ${trackHandleWidthClass} h-full`}
          ></div>
        </div>
      </div>
    </div>
  );
});

Scrollable.displayName = 'Scrollable';
